import React, { useState, useMemo, useEffect } from 'react';
import SharedCustomerBadge from './SharedCustomerBadge';
import IntakeEntryPoint from './intake/IntakeEntryPoint';
import CombineCustomerDialog from './CombineCustomerDialog';
import { useSavedViews } from '../hooks/useSavedViews';
import { useCustomCustomerOrder } from '../hooks/useCustomCustomerOrder';
import { useAuth } from '../contexts/AuthContext';

type DisplayMode = 'card' | 'list';
const DISPLAY_MODE_KEY = 'riskninja-customer-view-mode';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

const escapeCsvField = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/**
 * Pick the customer status dot color. Reflects the customer's persistent
 * status field only — activity recency is ignored so the dot doesn't change
 * out from under the user as time passes.
 *   active   → green
 *   lead     → blue
 *   inactive → gray
 */
const customerDotColor = (c: Pick<Customer, 'status'>): { className: string; title: string } => {
  if (c.status === 'inactive') {
    return { className: 'bg-gray-400', title: 'Inactive' };
  }
  if (c.status === 'lead') {
    return { className: 'bg-blue-500', title: 'Lead' };
  }
  return { className: 'bg-green-500', title: 'Active' };
};

const downloadCsv = (rows: string[][], filename: string) => {
  const csv = rows.map((r) => r.map(escapeCsvField).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export interface CustomerOwner {
  id: string;
  name: string;
  email: string;
}

export type CustomerPermission = 'viewer' | 'editor';

export interface Customer {
  id: string;
  name: string;
  type: 'customer' | 'prospect' | 'suspect';
  email?: string;
  phone?: string;
  company?: string;
  lineType?: 'personal' | 'commercial';
  secondaryContact?: string;
  createdAt: Date;
  lastContact?: Date;
  status: 'active' | 'inactive' | 'lead';
  isOwner?: boolean;
  accessType?: 'owned' | 'shared';
  owner?: CustomerOwner;
  shareCount?: number;
  permission?: CustomerPermission; // Effective permission for shared customers
  ams360CustomerId?: string | null;
  // Aggregate stats from /api/customers
  projectCount?: number;
  reportCount?: number;
  lastActivity?: string | null;
}

export interface ComparisonReport {
  id: string;
  title: string;
  content: string;
  documentNames: string[];
  documentIds: string[];
  primaryPolicyType: string;
  additionalFacts?: string;
  createdAt: Date;
  createdBy: string;
  projectId?: string;
}

export type CustomerSortOrder =
  | 'default'
  | 'custom'
  | 'alpha-asc'
  | 'alpha-desc'
  | 'newest'
  | 'oldest'
  | 'last-contact';

export interface CustomerFilters {
  lineType: 'all' | 'personal' | 'commercial';
  status: 'all' | 'active' | 'inactive' | 'lead';
  ownership: 'all' | 'mine' | 'shared';
  ams360: 'all' | 'synced' | 'unsynced';
}

export const DEFAULT_CUSTOMER_FILTERS: CustomerFilters = {
  lineType: 'all',
  status: 'all',
  ownership: 'all',
  ams360: 'all',
};

interface CustomerSelectionViewProps {
  selectedView: 'all' | 'customers' | 'prospects' | 'suspects';
  setSelectedView: (view: 'all' | 'customers' | 'prospects' | 'suspects') => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortOrder: CustomerSortOrder;
  setSortOrder: (order: CustomerSortOrder) => void;
  filters: CustomerFilters;
  setFilters: (filters: CustomerFilters) => void;
  loadingCustomers: boolean;
  isAms360Enabled: boolean;
  currentList: (Customer | ComparisonReport)[];
  onSelectCustomer: (customer: Customer) => void;
  onSelectReport: (report: ComparisonReport) => void;
  onConvertToCustomer: (id: string) => void;
  onEditCustomer: (customer: Customer) => void;
  onDeleteCustomer: (customer: Customer) => void;
  onShareCustomer: (customer: Customer) => void;
  onTransferCustomer: (customer: Customer) => void;
  onOpenChat: (customer: Customer) => void;
  onAddNew: (type: 'customer' | 'prospect' | 'suspect') => void;
  onOpenAMS360Import: () => void;
  onOpenGroupManagement: () => void;
  onShowAuditLog?: (customer: Customer) => void;
  onRefreshAfterBulk?: () => void;
}

const CustomerSelectionView: React.FC<CustomerSelectionViewProps> = ({
  selectedView,
  setSelectedView,
  searchTerm,
  setSearchTerm,
  sortOrder,
  setSortOrder,
  filters,
  setFilters,
  loadingCustomers,
  isAms360Enabled,
  currentList,
  onSelectCustomer,
  onSelectReport,
  onConvertToCustomer,
  onEditCustomer,
  onDeleteCustomer,
  onShareCustomer,
  onTransferCustomer,
  onOpenChat,
  onAddNew,
  onOpenAMS360Import,
  onOpenGroupManagement,
  onShowAuditLog,
  onRefreshAfterBulk,
}) => {
  const { token } = useAuth();
  const { views, saveView, deleteView } = useSavedViews();
  const { order: customOrder, setOrder: setCustomOrder, reorder } = useCustomCustomerOrder();
  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Multi-select state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Card vs list (file-explorer) display, persisted across sessions
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => {
    const saved = localStorage.getItem(DISPLAY_MODE_KEY);
    return saved === 'list' ? 'list' : 'card';
  });
  useEffect(() => {
    localStorage.setItem(DISPLAY_MODE_KEY, displayMode);
  }, [displayMode]);

  // Customer being merged into another (combine flow)
  const [combineSource, setCombineSource] = useState<Customer | null>(null);

  // Apply the user's manual ordering when 'custom' sort is active. Anything
  // not in the saved order falls to the end in its natural sequence — that
  // way newly-added customers stay visible without forcing the user to
  // re-arrange after every change.
  const orderedList = useMemo(() => {
    if (sortOrder !== 'custom' || customOrder.length === 0) return currentList;
    const indexById = new Map(customOrder.map((id, i) => [id, i]));
    return [...currentList].sort((a, b) => {
      const ai = indexById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bi = indexById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  }, [currentList, sortOrder, customOrder]);

  const visibleCustomers = useMemo(
    () => orderedList.filter((item): item is Customer => 'type' in item),
    [orderedList]
  );

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllVisible = () => {
    setSelectedIds(new Set(visibleCustomers.map((c) => c.id)));
  };

  const onBulkExportCsv = () => {
    const chosen = visibleCustomers.filter((c) => selectedIds.has(c.id));
    if (chosen.length === 0) return;
    const header = ['Name', 'Company', 'Contact', 'Type', 'Line', 'Status', 'Email', 'Phone', 'Created', 'Last activity', 'Projects', 'Reports'];
    const rows = chosen.map((c) => [
      c.lineType === 'personal' ? c.name : (c.company || c.name),
      c.company || '',
      c.company ? c.name : (c.secondaryContact || ''),
      c.type,
      c.lineType || '',
      c.status,
      c.email || '',
      c.phone || '',
      new Date(c.createdAt).toLocaleDateString(),
      c.lastActivity ? new Date(c.lastActivity).toLocaleDateString() : '',
      String(c.projectCount ?? 0),
      String(c.reportCount ?? 0),
    ]);
    downloadCsv([header, ...rows], `customers-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const onBulkArchive = async () => {
    const chosen = visibleCustomers.filter((c) => selectedIds.has(c.id));
    if (chosen.length === 0) return;
    if (!window.confirm(`Mark ${chosen.length} ${chosen.length === 1 ? 'customer' : 'customers'} as inactive? You can reactivate them individually later.`)) {
      return;
    }
    setBulkBusy(true);
    try {
      await Promise.all(
        chosen.map((c) =>
          fetch(`${API_BASE_URL}/api/customers/${c.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status: 'inactive' }),
          }).catch(() => null)
        )
      );
      clearSelection();
      onRefreshAfterBulk?.();
    } finally {
      setBulkBusy(false);
    }
  };

  const applyView = (view: typeof views[number]) => {
    setSortOrder(view.sortOrder);
    setFilters(view.filters);
    setSelectedView(view.selectedView);
    // If the view captured a manual order, restore it so 'custom' sort
    // re-applies that arrangement.
    if (view.sortOrder === 'custom' && view.customOrder && view.customOrder.length > 0) {
      setCustomOrder(view.customOrder);
    }
    setShowViewsMenu(false);
  };

  const hasActiveFilters =
    sortOrder !== 'default' ||
    filters.lineType !== 'all' ||
    filters.status !== 'all' ||
    filters.ownership !== 'all' ||
    filters.ams360 !== 'all';

  const selectedViewNoun =
    selectedView === 'customers' ? 'customers' :
    selectedView === 'prospects' ? 'prospects' :
    selectedView === 'suspects' ? 'suspects' :
    'customers';

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-secondary dark:text-dark-text">
          Customers
        </h1>
        <div className="flex items-center gap-4">
          {/* Add New Button */}
          <button
            onClick={() => {
              onAddNew(
                selectedView === 'prospects' ? 'prospect' :
                selectedView === 'suspects' ? 'suspect' :
                'customer'
              );
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Add {selectedView === 'prospects' ? 'Prospect' :
                 selectedView === 'suspects' ? 'Suspect' :
                 'Customer'}
          </button>

          {/* Import from AMS360 Button - only shown if AMS360 is enabled */}
          {isAms360Enabled && (
            <button
              onClick={onOpenAMS360Import}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,12L16,16H13V20H11V16H8L12,12Z" />
              </svg>
              Import from AMS360
            </button>
          )}

          {/* Manage Groups Button */}
          <button
            onClick={onOpenGroupManagement}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 17V19H2V17S2 13 9 13 16 17 16 17M12.5 7.5A3.5 3.5 0 1 0 9 11A3.5 3.5 0 0 0 12.5 7.5M15.94 13A5.32 5.32 0 0 1 18 17V19H22V17S22 13.37 15.94 13M15 4A3.39 3.39 0 0 0 13.07 4.59A5 5 0 0 1 13.07 10.41A3.39 3.39 0 0 0 15 11A3.5 3.5 0 0 0 15 4Z" />
            </svg>
            Manage Groups
          </button>
        </div>
      </div>

      <IntakeEntryPoint />

      {/* Search Bar, Filters and Sort */}
      <div className="mb-4 space-y-2">
        {/* Type filter chips — demoted from the old prominent tab row. "All"
            is the default unified list; the others just narrow it down. */}
        <div className="flex items-center gap-1.5">
          {(['all', 'customers', 'prospects', 'suspects'] as const).map((view) => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors capitalize ${
                selectedView === view
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-gray-200 dark:border-gray-700 text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={`Search ${selectedView === 'all' ? 'customers' : selectedView} by name, company, contact, email, phone…`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 pr-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-secondary dark:text-dark-text focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            )}
          </div>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as CustomerSortOrder)}
            aria-label="Sort order"
            className={`px-3 py-2 text-sm border rounded-lg transition-colors whitespace-nowrap bg-white dark:bg-dark-bg focus:ring-2 focus:ring-primary focus:border-primary ${
              sortOrder !== 'default'
                ? 'border-primary text-primary dark:text-blue-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
          >
            <option value="default">Sort: Default</option>
            <option value="custom">Custom (drag to reorder)</option>
            <option value="alpha-asc">Name A → Z</option>
            <option value="alpha-desc">Name Z → A</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="last-contact">Last contact</option>
          </select>

          {/* Card / List display toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex-shrink-0" role="group" aria-label="Display mode">
            <button
              type="button"
              onClick={() => setDisplayMode('card')}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${displayMode === 'card' ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
              title="Card view"
              aria-pressed={displayMode === 'card'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode('list')}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${displayMode === 'list' ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
              title="List view"
              aria-pressed={displayMode === 'list'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-1">Filter:</span>

          {/* Saved views dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowViewsMenu((v) => !v)}
              className="pl-3 pr-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 hover:border-primary transition-colors flex items-center gap-1"
              aria-haspopup="menu"
              aria-expanded={showViewsMenu}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14M5 12h14M5 19h7" />
              </svg>
              Views
              {views.length > 0 && (
                <span className="text-[10px] bg-gray-100 dark:bg-gray-700 px-1.5 rounded-full">{views.length}</span>
              )}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showViewsMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowViewsMenu(false)} />
                <div role="menu" className="absolute left-0 mt-1 w-72 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-xl z-40 py-1">
                  {views.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                      No saved views yet. Apply some filters then click "Save current view".
                    </div>
                  ) : (
                    <ul className="max-h-64 overflow-y-auto">
                      {views.map((v) => (
                        <li key={v.id} className="flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 group">
                          <button
                            type="button"
                            onClick={() => applyView(v)}
                            className="flex-1 text-left text-sm text-secondary dark:text-dark-text truncate"
                          >
                            {v.name}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteView(v.id); }}
                            aria-label={`Delete view ${v.name}`}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="border-t border-gray-200 dark:border-dark-border mt-1 pt-1">
                    <button
                      type="button"
                      disabled={!hasActiveFilters}
                      onClick={() => {
                        setShowViewsMenu(false);
                        setShowSaveDialog(true);
                        setNewViewName('');
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-primary hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={hasActiveFilters ? 'Save current sort + filters as a view' : 'Apply some filters first'}
                    >
                      + Save current view
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <select
            value={filters.lineType}
            onChange={(e) => setFilters({ ...filters, lineType: e.target.value as CustomerFilters['lineType'] })}
            aria-label="Filter by line of business"
            className={`pl-3 pr-7 py-1 text-sm border rounded-full bg-white dark:bg-dark-bg focus:ring-2 focus:ring-primary focus:border-primary ${
              filters.lineType !== 'all'
                ? 'border-primary text-primary dark:text-blue-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
          >
            <option value="all">Line: All</option>
            <option value="commercial">Commercial</option>
            <option value="personal">Personal</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value as CustomerFilters['status'] })}
            aria-label="Filter by status"
            className={`pl-3 pr-7 py-1 text-sm border rounded-full bg-white dark:bg-dark-bg focus:ring-2 focus:ring-primary focus:border-primary ${
              filters.status !== 'all'
                ? 'border-primary text-primary dark:text-blue-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
          >
            <option value="all">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="lead">Lead</option>
          </select>
          <select
            value={filters.ownership}
            onChange={(e) => setFilters({ ...filters, ownership: e.target.value as CustomerFilters['ownership'] })}
            aria-label="Filter by ownership"
            className={`pl-3 pr-7 py-1 text-sm border rounded-full bg-white dark:bg-dark-bg focus:ring-2 focus:ring-primary focus:border-primary ${
              filters.ownership !== 'all'
                ? 'border-primary text-primary dark:text-blue-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
          >
            <option value="all">Owner: All</option>
            <option value="mine">Mine</option>
            <option value="shared">Shared with me</option>
          </select>
          {isAms360Enabled && (
            <select
              value={filters.ams360}
              onChange={(e) => setFilters({ ...filters, ams360: e.target.value as CustomerFilters['ams360'] })}
              aria-label="Filter by AMS360 sync"
              className={`pl-3 pr-7 py-1 text-sm border rounded-full bg-white dark:bg-dark-bg focus:ring-2 focus:ring-primary focus:border-primary ${
                filters.ams360 !== 'all'
                  ? 'border-primary text-primary dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              <option value="all">AMS360: All</option>
              <option value="synced">Synced</option>
              <option value="unsynced">Not synced</option>
            </select>
          )}
          {(filters.lineType !== 'all' || filters.status !== 'all' || filters.ownership !== 'all' || filters.ams360 !== 'all') && (
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_CUSTOMER_FILTERS)}
              className="px-2.5 py-1 text-sm text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-blue-400 underline-offset-2 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar — appears when one or more customers are selected */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <span className="font-medium">{selectedIds.size}</span> selected
            {visibleCustomers.length > selectedIds.size && (
              <button
                type="button"
                onClick={selectAllVisible}
                className="ml-3 text-xs text-blue-700 dark:text-blue-300 hover:underline"
              >
                Select all visible ({visibleCustomers.length})
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBulkExportCsv}
              disabled={bulkBusy}
              className="px-3 py-1 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded hover:border-primary hover:text-primary dark:hover:text-blue-300"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={onBulkArchive}
              disabled={bulkBusy}
              className="px-3 py-1 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {bulkBusy ? 'Archiving…' : 'Archive'}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={bulkBusy}
              className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="min-h-[300px]">
        {loadingCustomers ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : displayMode === 'list' ? (
          /* ---- List (file-explorer) view ---- */
          currentList.length === 0 ? (
            <div className="text-center py-8 text-accent dark:text-dark-muted">
              No {selectedViewNoun} found. Click "Add {selectedView === 'prospects' ? 'Prospect' :
                                                    selectedView === 'suspects' ? 'Suspect' : 'Customer'}" to get started.
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Owner</th>
                    <th className="px-4 py-2 font-medium text-center">Projects</th>
                    <th className="px-4 py-2 font-medium text-center">Reports</th>
                    <th className="px-4 py-2 font-medium">Last activity</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {orderedList.map((item) => {
                    const isCustomer = 'type' in item;
                    if (!isCustomer) {
                      const rep = item as ComparisonReport;
                      return (
                        <tr key={rep.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer" onClick={() => onSelectReport(rep)}>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-purple-500 flex-shrink-0" />
                              <span className="font-medium text-secondary dark:text-dark-text truncate">{rep.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-gray-600 dark:text-gray-300">Report</td>
                          <td className="px-4 py-2 text-gray-400">—</td>
                          <td className="px-4 py-2 text-gray-400">—</td>
                          <td className="px-4 py-2 text-center text-gray-400">—</td>
                          <td className="px-4 py-2 text-center text-gray-400">—</td>
                          <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{new Date(rep.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-2" />
                        </tr>
                      );
                    }
                    const c = item as Customer;
                    const dot = customerDotColor(c);
                    const isOwner = c.isOwner !== false;
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer group" onClick={() => onSelectCustomer(c)}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot.className}`} title={dot.title} />
                            <span className="font-medium text-secondary dark:text-dark-text truncate">
                              {c.lineType === 'personal' ? c.name : (c.company || c.name)}
                            </span>
                            {!isOwner && c.owner && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">Shared</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-300 capitalize">{c.type}</td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-300 capitalize">{c.status}</td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-300 truncate">{isOwner ? 'You' : (c.owner?.name || '—')}</td>
                        <td className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">{c.projectCount ?? 0}</td>
                        <td className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">{c.reportCount ?? 0}</td>
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                          {c.lastActivity ? new Date(c.lastActivity).toLocaleDateString() : new Date(c.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={(e) => { e.stopPropagation(); onOpenChat(c); }} className="p-1.5 text-gray-400 hover:text-primary rounded" title="Chat">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                            </button>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {isOwner && (
                              <button onClick={(e) => { e.stopPropagation(); onShareCustomer(c); }} className="p-1.5 text-gray-400 hover:text-purple-600 rounded" title="Share">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>
                              </button>
                            )}
                            {isOwner && (
                              <button onClick={(e) => { e.stopPropagation(); setCombineSource(c); }} className="p-1.5 text-gray-400 hover:text-teal-600 rounded" title="Combine into another">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 8a3 3 0 01-3-3m3 3a3 3 0 00-3 3m13-3a3 3 0 003-3m-3 3a3 3 0 013 3M12 8v8m0 0a3 3 0 01-3 3m3-3a3 3 0 003 3" /></svg>
                              </button>
                            )}
                            {(isOwner || c.permission === 'editor') && (
                              <button onClick={(e) => { e.stopPropagation(); onEditCustomer(c); }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" title="Edit">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                            )}
                            {isOwner && (
                              <button onClick={(e) => { e.stopPropagation(); onDeleteCustomer(c); }} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orderedList.map((item) => {
              const isCustomer = 'type' in item;
              const isSelected = isCustomer && selectedIds.has(item.id);
              const customSort = sortOrder === 'custom';
              const isBeingDragged = draggingId === item.id;
              return (
              <div
                key={item.id}
                draggable={customSort && isCustomer}
                onDragStart={(e) => {
                  if (!customSort || !isCustomer) return;
                  setDraggingId(item.id);
                  e.dataTransfer.effectAllowed = 'move';
                  // Required for Firefox to register the drag
                  e.dataTransfer.setData('text/plain', item.id);
                }}
                onDragEnd={() => setDraggingId(null)}
                onDragOver={(e) => {
                  if (!customSort || !isCustomer || !draggingId || draggingId === item.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  if (!customSort || !isCustomer || !draggingId || draggingId === item.id) return;
                  e.preventDefault();
                  reorder(visibleCustomers.map((c) => c.id), draggingId, item.id);
                  setDraggingId(null);
                }}
                className={`bg-gray-50 dark:bg-gray-800 border ${
                  isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 dark:border-gray-700'
                } rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group relative ${
                  customSort && isCustomer ? 'cursor-grab active:cursor-grabbing' : ''
                } ${isBeingDragged ? 'opacity-50 ring-2 ring-primary' : ''}`}
                onClick={() => {
                  if ('type' in item) {
                    onSelectCustomer(item as Customer);
                  } else {
                    onSelectReport(item as ComparisonReport);
                  }
                }}
              >
                {'type' in item && onShowAuditLog && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowAuditLog(item as Customer);
                    }}
                    className="absolute bottom-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                    title="View change log"
                    aria-label="View change log"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="9" strokeWidth={2} />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4M12 8h.01" />
                    </svg>
                  </button>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Drag handle — appears only in Custom sort mode */}
                    {customSort && isCustomer && (
                      <span
                        className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                        aria-hidden="true"
                        title="Drag to reorder"
                      >
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.5" />
                          <circle cx="15" cy="6" r="1.5" />
                          <circle cx="9" cy="12" r="1.5" />
                          <circle cx="15" cy="12" r="1.5" />
                          <circle cx="9" cy="18" r="1.5" />
                          <circle cx="15" cy="18" r="1.5" />
                        </svg>
                      </span>
                    )}
                    {/* Status dot / select checkbox — checkbox takes over the dot slot whenever any selection is active or on hover */}
                    {isCustomer ? (
                      (() => {
                        const dot = customerDotColor(item as Customer);
                        const showCheckbox = selectedIds.size > 0 || isSelected;
                        return (
                          <div className="relative w-4 h-4 flex items-center justify-center flex-shrink-0">
                            {/* Status dot — hidden when checkbox is visible */}
                            <div
                              className={`absolute inset-0 m-auto w-3 h-3 rounded-full ${dot.className} transition-opacity ${
                                showCheckbox ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'
                              }`}
                              title={dot.title}
                              aria-label={dot.title}
                            />
                            {/* Checkbox — visible when selected or on hover */}
                            <label
                              onClick={(e) => e.stopPropagation()}
                              className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity ${
                                showCheckbox ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                              }`}
                              title={isSelected ? 'Deselect' : 'Select for bulk action'}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelected(item.id)}
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-500 text-primary focus:ring-primary"
                                aria-label={`Select ${(item as Customer).company || (item as Customer).name}`}
                              />
                            </label>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-purple-500 flex-shrink-0" title="Comparison report" />
                    )}
                    <h3 className="font-medium text-secondary dark:text-dark-text truncate">
                      {'type' in item ? ((item as Customer).lineType === 'personal' ? (item as Customer).name : ((item as Customer).company || (item as Customer).name)) : (item as ComparisonReport).title}
                    </h3>
                    {'type' in item && (item as Customer).type !== 'customer' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 capitalize flex-shrink-0">
                        {(item as Customer).type}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons Container */}
                  <div className="flex items-center gap-2">
                    {/* Chat bubble - always visible, available to owners and shared users alike */}
                    {'type' in item && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenChat(item as Customer);
                        }}
                        className="p-1.5 text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                        title={`Chat about ${(item as Customer).type}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </button>
                    )}
                    {/* Edit/Delete/Share buttons for customers - only takes up space on hover, so the
                        always-visible chat button above doesn't get pushed left by reserved dead space */}
                    {'type' in item && (
                      <div className="hidden group-hover:block">
                        <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 p-1">
                          {/* Share button - only for owners */}
                          {((item as Customer).isOwner !== false) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShareCustomer(item as Customer);
                              }}
                              className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
                              title={`Share ${(item as Customer).type}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                              </svg>
                            </button>
                          )}
                          {/* Transfer button - only for owners */}
                          {((item as Customer).isOwner !== false) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTransferCustomer(item as Customer);
                              }}
                              className="p-1.5 text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                              title={`Transfer ${(item as Customer).type}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </button>
                          )}
                          {/* Combine button - only for owners */}
                          {((item as Customer).isOwner !== false) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCombineSource(item as Customer);
                              }}
                              className="p-1.5 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded"
                              title={`Combine ${(item as Customer).type} into another`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 8a3 3 0 01-3-3m3 3a3 3 0 00-3 3m13-3a3 3 0 003-3m-3 3a3 3 0 013 3M12 8v8m0 0a3 3 0 01-3 3m3-3a3 3 0 003 3" />
                              </svg>
                            </button>
                          )}
                          {/* Edit button - owners and editor-share recipients */}
                          {((item as Customer).isOwner !== false || (item as Customer).permission === 'editor') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditCustomer(item as Customer);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title={`Edit ${(item as Customer).type}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {/* Delete button - only for owners */}
                          {((item as Customer).isOwner !== false) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteCustomer(item as Customer);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title={`Delete ${(item as Customer).type}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Convert button */}
                    {'type' in item && ((item as Customer).type === 'prospect' || (item as Customer).type === 'suspect') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onConvertToCustomer(item.id);
                        }}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        Convert
                      </button>
                    )}
                  </div>
                </div>

                {'type' in item ? (
                  <div className="space-y-1 text-sm text-accent dark:text-dark-muted">
                    {(item as Customer).lineType === 'personal' ? (
                      <>
                        {(item as Customer).secondaryContact && <p>Secondary: {(item as Customer).secondaryContact}</p>}
                      </>
                    ) : (
                      <>
                        {(item as Customer).company && <p>Contact: {(item as Customer).name}</p>}
                      </>
                    )}
                    {(item as Customer).email && <p>Email: {(item as Customer).email}</p>}
                    <p>Status: {(item as Customer).status}</p>
                    {/* Activity / volume stats */}
                    <div className="flex items-center gap-3 pt-1 text-xs text-gray-500 dark:text-gray-400">
                      <span title="Projects in this customer">
                        {(item as Customer).projectCount ?? 0} {(item as Customer).projectCount === 1 ? 'project' : 'projects'}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span title="Reports generated for this customer">
                        {(item as Customer).reportCount ?? 0} {(item as Customer).reportCount === 1 ? 'report' : 'reports'}
                      </span>
                    </div>
                    {(item as Customer).lastActivity ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Last activity: {new Date((item as Customer).lastActivity!).toLocaleDateString()}
                      </p>
                    ) : (
                      <p>Created: {new Date(item.createdAt).toLocaleDateString()}</p>
                    )}
                    {/* Shared badge for customers shared with the current user */}
                    {(item as Customer).isOwner === false && (item as Customer).owner && (
                      <div className="mt-2">
                        <SharedCustomerBadge owner={(item as Customer).owner!} compact />
                      </div>
                    )}
                    {/* Sharing badge for owned customers that are shared with others */}
                    {(item as Customer).isOwner !== false && ((item as Customer).shareCount ?? 0) > 0 && (
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Sharing with {(item as Customer).shareCount}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1 text-sm text-accent dark:text-dark-muted">
                    <p>Policy: {(item as ComparisonReport).primaryPolicyType}</p>
                    <p>Documents: {(item as ComparisonReport).documentNames.length}</p>
                    <p>Created: {new Date(item.createdAt).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              );
            })}

            {currentList.length === 0 && (
              <div className="col-span-full text-center py-8 text-accent dark:text-dark-muted">
                No {selectedViewNoun} found. Click "Add {selectedView === 'prospects' ? 'Prospect' :
                                                        selectedView === 'suspects' ? 'Suspect' :
                                                        'Customer'}" to get started.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save view dialog */}
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-secondary dark:text-dark-text mb-3">
              Save current view
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Captures the current sort, filters, and tab so you can jump back to this view in one click.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const name = newViewName.trim();
                if (!name) return;
                saveView({
                  name,
                  sortOrder,
                  filters,
                  selectedView,
                  customOrder: sortOrder === 'custom' ? customOrder : undefined,
                });
                setShowSaveDialog(false);
              }}
            >
              <input
                autoFocus
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="e.g. Active commercial customers"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 dark:text-white mb-4"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSaveDialog(false)}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newViewName.trim()}
                  className="flex-1 px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Combine (merge) customer dialog */}
      {combineSource && (
        <CombineCustomerDialog
          source={combineSource}
          onClose={() => setCombineSource(null)}
          onMerged={() => {
            setCombineSource(null);
            onRefreshAfterBulk?.();
          }}
        />
      )}
    </>
  );
};

export default CustomerSelectionView;
